/**
 * Invoice Reminders — who gets them, what they say, and what has gone out (admin).
 *
 * For the clients listed here, ElecBid sends the payment reminder instead of
 * Jobber: from a person's own mailbox, with the PO in the subject and the
 * invoice attached. Everything the page does goes through /api/invoice-reminders;
 * the rules live in api/_invoiceReminders.js. This file only edits the settings
 * row and shows what the server reports — it decides nothing about sending.
 *
 * Two things the page is careful about, because getting them wrong reaches a
 * customer:
 *   • Nothing is kept until Save, and Save can be refused. A mistyped address
 *     comes back as an error naming it; it is never dropped quietly.
 *   • "Opened" is never overstated. The server gives one of three answers
 *     (opened / possibly / nothing recorded) and the words to go with it, and
 *     this page prints those words as given.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/invoice-reminders";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0",
  muted: "#8892a4", red: "#ef4444", redDim: "rgba(239,68,68,.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,.12)", yellow: "#eab308", yellowDim: "rgba(234,179,8,.12)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,.12)",
};

const TABS = [
  ["clients", "Who gets them"],
  ["wording", "Wording"],
  ["sending", "When & from whom"],
  ["upcoming", "Coming up"],
  ["sent", "Sent & opened"],
];

// The words a reminder is built from, in the order they appear in the e-mail.
const WORDING_FIELDS = [
  ["subject_due", "Subject — on the due date", 1],
  ["subject_overdue", "Subject — after the due date", 1],
  ["greeting", "Greeting", 1],
  ["intro_due", "First paragraph — on the due date", 3],
  ["intro_overdue", "First paragraph — after the due date", 3],
  ["attachment_line", "Line about the attachment", 2],
  ["others_intro", "Line above the list of their other unpaid invoices", 2, "Only on a reminder day set to carry that list (under “When & from whom”)."],
  ["others_attachment_line", "Line about the copies of the listed invoices", 1, "Only when a copy of each listed invoice is attached."],
  ["closing", "Closing paragraph", 3],
  ["sign_off", "Sign-off", 1],
];
const TOKENS = [
  ["{invoice_number}", "1855"], ["{po}", "the PO number"], ["{po_dash}", "\" - PO 123\", or nothing when there is no PO"],
  ["{po_paren}", "\" (PO 123)\", or nothing"], ["{balance}", "$603.75"], ["{due_date}", "October 3, 2026"],
  ["{issued_date}", "September 3, 2026"], ["{days_phrase}", "\"4 days\""], ["{client_name}", "the client"], ["{company}", "your company name"],
];

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const clone = (o) => JSON.parse(JSON.stringify(o));
const looksLikeEmail = (a) => /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(String(a || ""));

// "2026-10-13" → "Tue Oct 13". Built at UTC noon so no time zone can move the day.
function fmtDay(day) {
  if (!day) return "";
  const d = new Date(`${String(day).slice(0, 10)}T12:00:00Z`);
  if (isNaN(d.getTime())) return String(day);
  return d.toLocaleDateString("en-CA", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
}
function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function addDaysStr(day, n) {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n, 12)).toISOString().slice(0, 10);
}
const offsetLabel = (n) => (Number(n) === 0 ? "on the due date" : `${n} day${Number(n) === 1 ? "" : "s"} after the due date`);

// The invoice copy arrives inside the preview answer as base64; the browser needs
// an address for it before it can show it or save it.
function pdfAddress(base64) {
  const bin = window.atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}
const fileSize = (n) => (Number(n) >= 1048576 ? `${(Number(n) / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((Number(n) || 0) / 1024))} KB`);
// Can this browser show a PDF inside the page? A phone mostly cannot — it hands
// the file to a viewer — and an empty grey box would read as "nothing attached".
const showsPdfInPage = () => typeof navigator !== "undefined" && navigator.pdfViewerEnabled === true && window.innerWidth >= 700;

// ── Small pieces ─────────────────────────────────────────────────────────────
function Btn({ children, onClick, disabled, kind = "ghost", size = "md", title }) {
  const styles = {
    ghost: { background: "transparent", color: C.text, border: `1px solid ${C.border}` },
    accent: { background: C.accent, color: "#0d0f12", border: `1px solid ${C.accent}`, fontWeight: 700 },
    blue: { background: C.blueDim, color: C.blue, border: `1px solid ${C.blue}` },
    danger: { background: C.redDim, color: C.red, border: "1px solid rgba(239,68,68,.4)" },
  }[kind];
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled} style={{
      ...styles, padding: size === "sm" ? "5px 10px" : "8px 14px", borderRadius: 8,
      fontSize: size === "sm" ? 12 : 13, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

// A link that looks like a Btn. A real link, so the browser opens or saves the
// file on the click itself — nothing for a pop-up blocker to stop.
function LinkBtn({ children, href, download, kind = "ghost" }) {
  const styles = {
    ghost: { background: "transparent", color: C.text, border: `1px solid ${C.border}` },
    blue: { background: C.blueDim, color: C.blue, border: `1px solid ${C.blue}` },
  }[kind];
  return (
    <a href={href} download={download || undefined} target={download ? undefined : "_blank"} rel="noopener noreferrer" style={{
      ...styles, display: "inline-block", padding: "8px 14px", borderRadius: 8, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap",
    }}>{children}</a>
  );
}

// The whole row is the switch: the label and the hint are as clickable as the knob.
function Toggle({ on, onChange, label, hint, disabled }) {
  const flip = () => { if (!disabled) onChange(!on); };
  return (
    <div
      role="switch" aria-checked={!!on} aria-label={typeof label === "string" ? label : undefined} tabIndex={disabled ? -1 : 0}
      onClick={flip}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); flip(); } }}
      style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1, userSelect: "none" }}>
      <span style={{
        flex: "0 0 auto", width: 38, height: 22, borderRadius: 999, marginTop: 1, position: "relative",
        background: on ? C.accent : C.surface2, border: `1px solid ${on ? C.accent : C.border}`, transition: "background .15s",
      }}>
        <span style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: 999, background: on ? "#0d0f12" : C.muted, transition: "left .15s" }} />
      </span>
      <span>
        <span style={{ fontSize: 13.5, color: C.text, fontWeight: 600 }}>{label}</span>
        {hint && <span style={{ display: "block", fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.45 }}>{hint}</span>}
      </span>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box", background: C.bg, color: C.text, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: "8px 10px", fontSize: 13.5, fontFamily: "inherit", outline: "none",
};

function Card({ children, style }) {
  return <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 14, ...(style || {}) }}>{children}</div>;
}

function Pill({ children, color = C.muted, bg = "rgba(136,146,164,.12)", title }) {
  return <span title={title} style={{ display: "inline-block", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3, padding: "3px 9px", borderRadius: 999, background: bg, color, whiteSpace: "nowrap" }}>{children}</span>;
}

// A list of e-mail addresses as chips. An address is only added once it looks
// like one; whatever is half-typed stays in the box, in red, rather than vanish.
function EmailChips({ value, onChange, placeholder, suggestions }) {
  const [text, setText] = useState("");
  const list = Array.isArray(value) ? value : [];
  const bad = text.trim() !== "" && !looksLikeEmail(text.trim());
  const add = (raw) => {
    const parts = String(raw || "").split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const good = parts.filter(looksLikeEmail);
    const rest = parts.filter((p) => !looksLikeEmail(p));
    const next = [...list];
    for (const g of good) if (!next.includes(g)) next.push(g);
    if (next.length !== list.length) onChange(next);
    setText(rest.join(" "));
  };
  const more = (suggestions || []).filter((s) => !list.includes(s));
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", background: C.bg, border: `1px solid ${bad ? C.red : C.border}`, borderRadius: 8, padding: "6px 8px" }}>
        {list.map((a) => (
          <span key={a} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: 12.5, color: C.text }}>
            {a}
            <button type="button" aria-label={`Remove ${a}`} onClick={() => onChange(list.filter((x) => x !== a))}
              style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>×</button>
          </span>
        ))}
        <input
          value={text} placeholder={list.length ? "" : placeholder}
          onChange={(e) => { const v = e.target.value; if (/[,;\s]$/.test(v)) add(v); else setText(v); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(text); }
            else if (e.key === "Backspace" && !text && list.length) onChange(list.slice(0, -1));
          }}
          onBlur={() => add(text)}
          style={{ flex: "1 1 160px", minWidth: 120, background: "transparent", border: "none", outline: "none", color: bad ? C.red : C.text, fontSize: 13.5, padding: "3px 2px", fontFamily: "inherit" }}
        />
      </div>
      {bad && <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>That is not a complete e-mail address yet — it has not been added.</div>}
      {more.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: C.muted, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          On this client in Jobber:
          {more.map((s) => (
            <button key={s} type="button" onClick={() => onChange([...list, s])}
              style={{ background: "transparent", border: `1px dashed ${C.border}`, color: C.muted, borderRadius: 999, padding: "2px 9px", fontSize: 12, cursor: "pointer" }}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const OPEN_STYLE = {
  opened: { color: C.green, bg: C.greenDim, word: "Opened" },
  maybe: { color: C.yellow, bg: C.yellowDim, word: "Possibly opened" },
  none: { color: C.muted, bg: "rgba(136,146,164,.12)", word: "No open recorded" },
};
const STATUS_STYLE = {
  sent: { color: C.green, bg: C.greenDim, word: "Sent" },
  held: { color: C.yellow, bg: C.yellowDim, word: "Held" },
  failed: { color: C.red, bg: C.redDim, word: "Not sent" },
  claimed: { color: C.blue, bg: C.blueDim, word: "Sending" },
};
const VIA_WORD = { outlook: "Outlook", browser: "web / phone mail app", gmail: "Gmail", yahoo: "Yahoo Mail", apple: "Apple Mail", other: "unrecognised mail program" };

// ── The page ─────────────────────────────────────────────────────────────────
export default function InvoiceReminders() {
  const perms = usePermissions() || {};
  const allowed = !!(perms.isAdmin || perms.isOwner);

  const [view, setView] = useState(null);         // what the server last gave us
  const [draft, setDraft] = useState(null);       // what is being edited
  const [tab, setTab] = useState("clients");
  const [jobber, setJobber] = useState({});       // client_id → Jobber's own follow-up switch
  const [activity, setActivity] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [upcomingBusy, setUpcomingBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);           // { kind: "ok" | "err", text }
  const [errors, setErrors] = useState([]);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState(null);
  const [jobberEmails, setJobberEmails] = useState({});   // client_id → addresses on the Jobber client
  const [sample, setSample] = useState(null);
  const sampleFiles = useRef([]);                 // browser addresses of the previewed invoice copies
  const [sampleInvoice, setSampleInvoice] = useState("");
  const [sampleOffset, setSampleOffset] = useState("0");
  const [openRow, setOpenRow] = useState(null);
  const [advanced, setAdvanced] = useState({});

  const call = useCallback(async (qs, opts) => {
    try {
      const r = await apiFetch(`${API}?${qs}`, opts);
      let j = {};
      try { j = await r.json(); } catch { j = {}; }
      if (!r.ok && !j.error) j.error = `The server answered ${r.status}`;
      if (!r.ok) j.ok = false;
      return j;
    } catch (e) {
      return { ok: false, error: String((e && e.message) || e) };
    }
  }, []);
  const post = useCallback((action, body) => call(`action=${action}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
  }), [call]);

  const loadSettings = useCallback(async () => {
    const j = await call("action=settings");
    if (j.ok) { setView(j); setDraft(clone(j.settings)); setErrors([]); }
    else setMsg({ kind: "err", text: j.error || "Could not load the settings." });
    return j;
  }, [call]);
  const loadJobber = useCallback(async () => {
    const j = await call("action=jobber-followups");
    if (!j.ok) return;
    const map = {};
    for (const c of j.clients || []) map[c.client_id] = c.error ? { error: c.error } : c.followUps;
    setJobber(map);
  }, [call]);
  const loadActivity = useCallback(async () => {
    const j = await call("action=status&limit=100");
    if (j.recent) setActivity(j.recent);
  }, [call]);
  const loadUpcoming = useCallback(async () => {
    setUpcomingBusy(true);
    const j = await call("action=preview");
    setUpcomingBusy(false);
    if (j.ok) setUpcoming(j);
    else setMsg({ kind: "err", text: j.error || "Could not read the open invoices from Jobber." });
  }, [call]);

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    (async () => {
      setLoading(true);
      await loadSettings();
      if (!live) return;
      setLoading(false);
      loadJobber();
      loadActivity();
    })();
    return () => { live = false; };
  }, [allowed, loadSettings, loadJobber, loadActivity]);

  // The previewed invoice copies are held by the browser under addresses; hand
  // them back when the page is left.
  useEffect(() => () => { for (const url of sampleFiles.current) URL.revokeObjectURL(url); }, []);

  // Reading every open invoice out of Jobber takes a few seconds, so it waits
  // until a tab that needs it is opened.
  useEffect(() => {
    if (allowed && (tab === "upcoming" || tab === "wording") && !upcoming && !upcomingBusy) loadUpcoming();
  }, [allowed, tab, upcoming, upcomingBusy, loadUpcoming]);

  const openInvoices = useMemo(() => {
    const out = [];
    for (const c of (upcoming && upcoming.clients) || []) for (const i of c.invoices || []) out.push({ ...i, client: c.name });
    return out;
  }, [upcoming]);
  useEffect(() => {
    if (!sampleInvoice && openInvoices.length) setSampleInvoice(String(openInvoices[0].invoice));
  }, [openInvoices, sampleInvoice]);

  const dirty = useMemo(() => !!(view && draft && JSON.stringify(draft) !== JSON.stringify(view.settings)), [view, draft]);
  const savedIds = useMemo(() => new Set(((view && view.settings && view.settings.clients) || []).map((c) => c.client_id)), [view]);

  if (!allowed) {
    return <div style={{ padding: 40, color: C.muted, background: C.bg, minHeight: "100vh" }}>Invoice Reminders is admin-only.</div>;
  }
  if (loading || !draft || !view) {
    return (
      <div style={{ padding: 40, color: C.muted, background: C.bg, minHeight: "100vh" }}>
        {msg && msg.kind === "err" ? msg.text : "Loading…"}
      </div>
    );
  }

  // ── Editing ────────────────────────────────────────────────────────────────
  const patch = (obj) => setDraft((d) => ({ ...d, ...obj }));
  const patchClient = (i, obj) => setDraft((d) => ({ ...d, clients: d.clients.map((c, k) => (k === i ? { ...c, ...obj } : c)) }));
  const patchWording = (k, v) => setDraft((d) => ({ ...d, wording: { ...d.wording, [k]: v } }));
  const patchCompany = (k, v) => setDraft((d) => ({ ...d, company: { ...(d.company || {}), [k]: v } }));
  const clock = view.clock || {};
  const suggestedStart = clock.today ? (Number(clock.hour) < Number(draft.send_hour_local) ? clock.today : addDaysStr(clock.today, 1)) : "";

  const save = async () => {
    const turningOn = draft.enabled && !view.settings.enabled;
    if (turningOn) {
      const live = draft.clients.filter((c) => c.enabled);
      const lines = live.map((c) => `• ${c.name} → ${c.to.join(", ") || "(nobody yet)"}${c.active_from ? `, from ${fmtDay(c.active_from)}` : " — NO start date, so nothing will go"}`);
      const stillOn = live.filter((c) => jobber[c.client_id] && jobber[c.client_id].anyOn).map((c) => c.name);
      const ok = window.confirm(
        `Switch invoice reminders ON?\n\nFrom then on ElecBid e-mails these customers from ${draft.from_email}:\n${lines.join("\n") || "• (no clients switched on)"}` +
        (stillOn.length ? `\n\nJobber's own reminder is still ON for ${stillOn.join(" and ")}. Until it is switched off, their reminders are held here so they never get two.` : "")
      );
      if (!ok) return;
    }
    setBusy("save"); setErrors([]); setMsg(null);
    const j = await post("save-settings", { settings: draft });
    setBusy("");
    if (j.ok) {
      setView(j); setDraft(clone(j.settings));
      setMsg({ kind: "ok", text: j.settings.enabled ? "Saved. Reminders are ON." : "Saved. Reminders are OFF — nothing is being sent." });
      loadJobber();
    } else {
      setErrors(j.errors || []);
      setMsg({ kind: "err", text: j.error || "Nothing was saved." });
    }
  };
  const discard = () => { setDraft(clone(view.settings)); setErrors([]); setMsg(null); };

  const switchJobber = async (i, c, on) => {
    const ask = on
      ? `Switch Jobber's own invoice reminder back ON for ${c.name}?\n\nJobber will go back to e-mailing them "Your invoice is due today".`
      : `Switch Jobber's own invoice reminder OFF for ${c.name}?\n\nJobber stops e-mailing them about unpaid invoices. Do this when you are ready for ElecBid to send the reminders instead — until this page is switched ON with a start date for them, nobody is reminding this client.`;
    if (!window.confirm(ask)) return;
    setBusy(`jobber:${c.client_id}`); setMsg(null);
    const j = await post("jobber-followups", { client_id: c.client_id, on });
    setBusy("");
    if (!j.ok) { setMsg({ kind: "err", text: j.error || (j.clients && j.clients[0] && j.clients[0].error) || "Jobber did not accept the change." }); return; }
    await loadJobber();
    if (!on && !c.active_from && suggestedStart) {
      patchClient(i, { active_from: suggestedStart });
      setMsg({ kind: "ok", text: `Jobber's reminder is off for ${c.name}. Start date set to ${fmtDay(suggestedStart)} — check it, then Save.` });
    } else {
      setMsg({ kind: "ok", text: `Jobber's own reminder is now ${on ? "ON" : "OFF"} for ${c.name}.` });
    }
  };

  const findClient = async () => {
    if (search.trim().length < 2) return;
    setBusy("find"); setFound(null);
    const j = await call(`action=find-client&q=${encodeURIComponent(search.trim())}`);
    setBusy("");
    if (j.ok) setFound(j.clients || []);
    else setMsg({ kind: "err", text: j.error || "Could not search Jobber." });
  };
  const addClient = (f) => {
    setJobberEmails((m) => ({ ...m, [f.client_id]: f.emails || [] }));
    setDraft((d) => ({
      ...d,
      clients: [...d.clients, {
        client_id: f.client_id, name: f.name, enabled: true, active_from: null, to: [], cc: [], bcc: [],
        require_po: false, po_pattern: null, hold_subject_pattern: view.standardHoldPattern, skip_invoices: [], pdf: {},
      }],
    }));
    setFound(null); setSearch("");
  };
  const removeClient = (i, c) => {
    if (!window.confirm(`Take ${c.name} off this list?\n\nElecBid stops sending their reminders once you Save. If Jobber's own reminder is off for them, switch it back on first — otherwise nobody reminds them.`)) return;
    setDraft((d) => ({ ...d, clients: d.clients.filter((_, k) => k !== i) }));
  };

  const renderSample = async () => {
    if (!sampleInvoice.trim()) { setMsg({ kind: "err", text: "Type an invoice number to preview with." }); return; }
    setBusy("sample"); setMsg(null);
    const j = await post("render-sample", { invoice: sampleInvoice.trim(), offset: sampleOffset === "today" ? undefined : Number(sampleOffset), settings: draft });
    setBusy("");
    for (const url of sampleFiles.current) URL.revokeObjectURL(url);
    sampleFiles.current = [];
    if (!j.ok) { setSample(null); setMsg({ kind: "err", text: j.error || "Could not build the preview." }); return; }
    // The answer carries the copies themselves, drawn as a send would draw them,
    // so the preview can show what is attached and not only name it: the
    // invoice's own copy, then — on a day that lists the client's other unpaid
    // invoices — one for each of those.
    const hold = (p) => {
      try {
        const url = pdfAddress(p.base64);
        sampleFiles.current.push(url);
        return { url, name: p.filename, bytes: p.bytes, pages: p.pages };
      } catch { return null; }
    };
    const own = j.pdf && j.pdf.base64 ? hold(j.pdf) : null;
    const attached = own ? { ...own, name: own.name || j.attachment, inPage: showsPdfInPage() } : null;
    const alsoAttached = ((j.others && j.others.files) || []).map(hold).filter(Boolean);
    const others = j.others ? { error: j.others.error || null, listed: j.others.listed || [], leftOut: j.others.leftOut || [] } : null;
    setSample({ ...j, pdf: undefined, others, attached, alsoAttached });
  };
  const emailSample = async (invoice, offset) => {
    setBusy(`mail:${invoice}`); setMsg(null);
    const j = await post("test", { invoice: String(invoice), offset: offset === "today" || offset === undefined ? undefined : Number(offset), settings: dirty ? draft : undefined });
    setBusy("");
    setMsg(j.ok
      ? { kind: "ok", text: `A sample for invoice #${invoice} is in your inbox (${(j.sentTo || []).join(", ")}) with ${j.attachment ? `“${j.attachment}”` : "the invoice"} attached${j.pdfBytes ? ` (${fileSize(j.pdfBytes)})` : ""}${j.alsoAttached ? `, plus ${j.alsoAttached} more ${j.alsoAttached === 1 ? "copy" : "copies"} for the other invoices it lists` : ""}. Nothing went to the customer.` }
      : { kind: "err", text: j.error || "The sample could not be sent." });
  };
  const viewCopy = async (invoice, offset) => {
    // The tab is opened inside the click itself. One opened after the wait for
    // Jobber is exactly what a pop-up blocker stops — and then the button looks dead.
    let tab = null;
    try { tab = window.open("", "_blank"); } catch { tab = null; }
    if (tab) { try { tab.document.title = `Invoice #${invoice}`; tab.document.body.textContent = "Drawing the invoice copy…"; } catch { /* only a courtesy */ } }
    setBusy(`pdf:${invoice}`); setMsg(null);
    let problem = null;
    try {
      const r = await apiFetch(`${API}?action=pdf&invoice=${encodeURIComponent(invoice)}${offset === undefined || offset === "today" ? "" : `&offset=${Number(offset)}`}`);
      if (!r.ok) {
        let j = {};
        try { j = await r.json(); } catch { j = {}; }
        problem = j.error || "The invoice copy could not be drawn.";
      } else {
        const url = URL.createObjectURL(await r.blob());
        if (tab && !tab.closed) tab.location.replace(url);
        // No tab to put it in: leave a link, which the browser will always follow.
        else setMsg({ kind: "ok", text: `The copy of invoice #${invoice} is ready — the browser did not open a new tab for it.`, link: { href: url, label: "Open the invoice copy" } });
        // Kept long enough to be read, saved or printed from that tab.
        setTimeout(() => URL.revokeObjectURL(url), 600000);
      }
    } catch (e) {
      problem = String((e && e.message) || e);
    }
    if (problem) {
      if (tab && !tab.closed) tab.close();
      setMsg({ kind: "err", text: problem });
    }
    setBusy("");
  };

  // ── What state are we in, in one line ──────────────────────────────────────
  const on = !!view.settings.enabled;
  const liveClients = view.settings.clients.filter((c) => c.enabled && c.active_from);
  const uncovered = view.settings.clients.filter((c) => jobber[c.client_id] && !jobber[c.client_id].error && !jobber[c.client_id].anyOn && !(on && c.enabled && c.active_from));

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, padding: "24px 20px 80px" }}>
      <div style={{ maxWidth: 940, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 420px" }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Invoice Reminders</h1>
            <p style={{ color: C.muted, fontSize: 13, margin: "6px 0 0", maxWidth: 620, lineHeight: 1.5 }}>
              For the clients listed here, ElecBid sends the payment reminder instead of Jobber — from a person's own mailbox,
              with the <b style={{ color: C.text }}>PO number in the subject</b> and the <b style={{ color: C.text }}>invoice attached</b>,
              on the same days Jobber would have. Every other client keeps getting Jobber's reminder.
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <Pill color={on ? C.green : C.muted} bg={on ? C.greenDim : "rgba(136,146,164,.14)"}>{on ? "On" : "Off — nothing is being sent"}</Pill>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
              {on ? `${liveClients.length} client${liveClients.length === 1 ? "" : "s"} live · from ${view.settings.from_email || "—"}` : "Switch on under “When & from whom”"}
            </div>
          </div>
        </div>

        {/* The gap nobody would otherwise notice */}
        {uncovered.length > 0 && (
          <div style={{ marginTop: 14, background: C.redDim, border: "1px solid rgba(239,68,68,.4)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: C.text, lineHeight: 1.5 }}>
            <b style={{ color: C.red }}>Nobody is reminding {uncovered.map((c) => c.name).join(" or ")}.</b> Jobber's own reminder is switched off for
            {uncovered.length === 1 ? " this client" : " these clients"} and ElecBid's are not going to them yet
            ({!on ? "this page is switched off" : "no start date, or the client is switched off here"}).
          </div>
        )}
        {view.problems && view.problems.length > 0 && !dirty && (
          <div style={{ marginTop: 14, background: C.yellowDim, border: "1px solid rgba(234,179,8,.35)", borderRadius: 10, padding: "10px 14px", fontSize: 13, lineHeight: 1.5 }}>
            <b style={{ color: C.yellow }}>{on ? "Nothing will be sent until this is fixed:" : "Before this can be switched on:"}</b>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: C.text }}>{view.problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
          {TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} style={{
              background: tab === k ? C.accentDim : "transparent", color: tab === k ? C.accent : C.muted,
              border: `1px solid ${tab === k ? C.accent : C.border}`, borderRadius: 999, padding: "6px 15px", fontSize: 13, cursor: "pointer",
            }}>{label}{k === "sent" && activity ? ` (${activity.filter((r) => r.status === "sent").length})` : ""}</button>
          ))}
        </div>

        {msg && (
          <div style={{ marginTop: 12, fontSize: 13, padding: "8px 12px", borderRadius: 8, lineHeight: 1.5,
            background: msg.kind === "ok" ? C.greenDim : C.redDim, color: msg.kind === "ok" ? C.green : C.red }}>
            {msg.text}
            {msg.link && <>{" "}<a href={msg.link.href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>{msg.link.label}</a></>}
          </div>
        )}
        {errors.length > 0 && (
          <div style={{ marginTop: 10, background: C.redDim, border: "1px solid rgba(239,68,68,.4)", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
            <ul style={{ margin: 0, paddingLeft: 18, color: C.text, lineHeight: 1.6 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
          </div>
        )}

        <div style={{ marginTop: 16 }}>

          {/* ── Who gets them ─────────────────────────────────────────────── */}
          {tab === "clients" && (
            <div>
              {draft.clients.length === 0 && <Card><span style={{ color: C.muted, fontSize: 13.5 }}>No clients yet. Find one below to start.</span></Card>}
              {draft.clients.map((c, i) => {
                const j = jobber[c.client_id];
                const isSaved = savedIds.has(c.client_id);
                const jobberOn = j && !j.error && j.anyOn;
                const adv = !!advanced[c.client_id];
                return (
                  <Card key={c.client_id}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>{c.name}</div>
                        <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          {!isSaved ? <Pill color={C.blue} bg={C.blueDim}>New — not saved yet</Pill>
                            : !j ? <Pill>Checking Jobber…</Pill>
                              : j.error ? <Pill color={C.red} bg={C.redDim} title={j.error}>Could not read Jobber</Pill>
                                : jobberOn ? <Pill color={C.yellow} bg={C.yellowDim}>Jobber's own reminder: ON</Pill>
                                  : <Pill color={C.green} bg={C.greenDim}>Jobber's own reminder: off</Pill>}
                          {isSaved && j && !j.error && (
                            <Btn size="sm" disabled={busy === `jobber:${c.client_id}`} onClick={() => switchJobber(i, c, !jobberOn)}>
                              {busy === `jobber:${c.client_id}` ? "Changing…" : jobberOn ? "Switch Jobber's off" : "Switch Jobber's back on"}
                            </Btn>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Toggle on={c.enabled} onChange={(v) => patchClient(i, { enabled: v })} label={c.enabled ? "Sending" : "Paused"} />
                        <Btn size="sm" kind="danger" onClick={() => removeClient(i, c)}>Remove</Btn>
                      </div>
                    </div>

                    {isSaved && jobberOn && c.enabled && (
                      <div style={{ marginTop: 10, fontSize: 12.5, color: C.yellow, lineHeight: 1.5 }}>
                        While Jobber's own reminder is on, nothing goes out from here for this client — they would get two. Each reminder is held and the office is told.
                      </div>
                    )}

                    <div style={{ marginTop: 14 }}>
                      <Field label="To" hint="Who the reminder is addressed to — normally their accounts-payable inbox.">
                        <EmailChips value={c.to} onChange={(v) => patchClient(i, { to: v })} placeholder="ap@theircompany.com" suggestions={jobberEmails[c.client_id]} />
                      </Field>
                      <Field label="Cc" hint="Copied on every reminder — the buyer, the project manager, anyone who chases it internally.">
                        <EmailChips value={c.cc} onChange={(v) => patchClient(i, { cc: v })} placeholder="Add an address and press Enter" suggestions={(jobberEmails[c.client_id] || []).filter((s) => !c.to.includes(s))} />
                      </Field>

                      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <div style={{ flex: "0 0 210px" }}>
                          <Field label="ElecBid takes over from" hint="A reminder due before this date is left to Jobber, so none is sent twice.">
                            <div style={{ display: "flex", gap: 6 }}>
                              <input type="date" value={c.active_from || ""} onChange={(e) => patchClient(i, { active_from: e.target.value || null })} style={{ ...inputStyle, colorScheme: "dark" }} />
                              {!c.active_from && suggestedStart && <Btn size="sm" onClick={() => patchClient(i, { active_from: suggestedStart })} title="Today if today's reminders have not gone yet, otherwise tomorrow">{fmtDay(suggestedStart)}</Btn>}
                            </div>
                          </Field>
                        </div>
                        <div style={{ flex: "1 1 300px", display: "grid", gap: 12, paddingTop: 2 }}>
                          <Toggle on={c.require_po} onChange={(v) => patchClient(i, { require_po: v })}
                            label="Never send without a PO number"
                            hint="If no PO is found on the invoice or its job, the reminder is held and the office is e-mailed instead." />
                          <Toggle on={!!c.hold_subject_pattern} onChange={(v) => patchClient(i, { hold_subject_pattern: v ? view.standardHoldPattern : null })}
                            label="Hold when the invoice subject says “waiting for PO”"
                            hint="Lets the office park one invoice from Jobber, just by writing it in the subject." />
                        </div>
                      </div>

                      <button type="button" onClick={() => setAdvanced((a) => ({ ...a, [c.client_id]: !adv }))}
                        style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 12.5, padding: 0, marginTop: 4 }}>
                        {adv ? "▾ Fewer options" : "▸ More options"}
                      </button>
                      {adv && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                          <Field label="Bcc" hint="Blind-copied; the customer does not see these.">
                            <EmailChips value={c.bcc} onChange={(v) => patchClient(i, { bcc: v })} placeholder="Optional" />
                          </Field>
                          <Field label="What their PO numbers look like" hint="A pattern (regular expression). Needed when they write the PO with no “PO” in front of it, e.g. 29PO00003204 on its own in the invoice subject. Leave empty to look for “PO 12345”, “PO# 12345” and the PO# field.">
                            <input value={c.po_pattern || ""} onChange={(e) => patchClient(i, { po_pattern: e.target.value || null })} placeholder="e.g. \b\d{2}PO\d{6,}\b" style={{ ...inputStyle, fontFamily: "ui-monospace, Menlo, Consolas, monospace" }} />
                          </Field>
                          <Field label="Invoices to leave alone" hint="Invoice numbers that should never get a reminder, separated by commas.">
                            <input value={(c.skip_invoices || []).join(", ")} onChange={(e) => patchClient(i, { skip_invoices: e.target.value.split(/[,\s]+/).map((s) => s.replace(/^#/, "").trim()).filter(Boolean) })} placeholder="e.g. 1831" style={inputStyle} />
                          </Field>
                          <Field label="Service address on the invoice copy" hint="Jobber prints a separate service address on some clients' invoices even when it matches the billing address.">
                            <select value={(c.pdf && c.pdf.service_address) || "auto"} onChange={(e) => patchClient(i, { pdf: { ...(c.pdf || {}), service_address: e.target.value } })} style={{ ...inputStyle, width: "auto" }}>
                              <option value="auto">Only when it differs from the billing address</option>
                              <option value="always">Always</option>
                              <option value="never">Never</option>
                            </select>
                          </Field>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}

              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Add a client</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") findClient(); }}
                    placeholder="Client name, as it is in Jobber" style={{ ...inputStyle, flex: "1 1 260px", width: "auto" }} />
                  <Btn onClick={findClient} disabled={busy === "find" || search.trim().length < 2}>{busy === "find" ? "Searching…" : "Find in Jobber"}</Btn>
                </div>
                {found && found.length === 0 && <div style={{ fontSize: 13, color: C.muted, marginTop: 10 }}>No client by that name in Jobber.</div>}
                {found && found.length > 0 && (
                  <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                    {found.map((f) => {
                      const have = draft.clients.some((c) => c.client_id === f.client_id);
                      return (
                        <div key={f.client_id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{f.name}</div>
                            <div style={{ fontSize: 12, color: C.muted }}>{(f.emails || []).join(", ") || "no e-mail on file"}{Number(f.balance) > 0 ? ` · owes ${money(f.balance)}` : ""}</div>
                          </div>
                          <Btn size="sm" kind="blue" disabled={have} onClick={() => addClient(f)}>{have ? "Already listed" : "Add"}</Btn>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── Wording ───────────────────────────────────────────────────── */}
          {tab === "wording" && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 400px", minWidth: 0 }}>
                <Card>
                  {WORDING_FIELDS.map(([k, label, rows, hint]) => {
                    const std = (view.standardWording || {})[k] || "";
                    const val = draft.wording && draft.wording[k] !== undefined ? draft.wording[k] : std;
                    const changed = val !== std;
                    return (
                      <Field key={k} label={label} hint={hint}>
                        {rows === 1
                          ? <input value={val} onChange={(e) => patchWording(k, e.target.value)} style={inputStyle} />
                          : <textarea value={val} rows={rows} onChange={(e) => patchWording(k, e.target.value)} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }} />}
                        {changed && (
                          <button type="button" onClick={() => patchWording(k, std)} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, padding: "4px 0 0" }}>
                            ↺ Back to the standard wording
                          </button>
                        )}
                      </Field>
                    );
                  })}
                  <Field label="Signature" hint="One line per row, under the sign-off.">
                    <textarea value={(draft.signature || []).join("\n")} rows={3} onChange={(e) => patch({ signature: e.target.value.split("\n") })} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }} />
                  </Field>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                    <b style={{ color: C.text }}>These are filled in for each invoice:</b><br />
                    {TOKENS.map(([t, what]) => <span key={t} style={{ display: "inline-block", marginRight: 12 }}><code style={{ color: C.accent }}>{t}</code> {what}</span>)}
                    <div style={{ marginTop: 6 }}>The block listing invoice number, PO, dates and amount is always included between the first paragraph and the attachment line.</div>
                  </div>
                </Card>
              </div>

              <div style={{ flex: "1 1 400px", minWidth: 0 }}>
                <Card>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>See it on a real invoice</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {openInvoices.length > 0 ? (
                      <select value={sampleInvoice} onChange={(e) => setSampleInvoice(e.target.value)} style={{ ...inputStyle, width: "auto", flex: "1 1 190px" }}>
                        {openInvoices.map((o) => <option key={o.invoice} value={o.invoice}>#{o.invoice} · {o.client} · {money(o.balance)}</option>)}
                      </select>
                    ) : (
                      <input value={sampleInvoice} onChange={(e) => setSampleInvoice(e.target.value)} placeholder={upcomingBusy ? "Reading open invoices…" : "Invoice number"} style={{ ...inputStyle, width: "auto", flex: "1 1 150px" }} />
                    )}
                    <select value={sampleOffset} onChange={(e) => setSampleOffset(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
                      {(draft.offsets_days || []).map((n) => <option key={n} value={String(n)}>As sent {offsetLabel(n)}</option>)}
                      <option value="today">As if sent today</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <Btn kind="blue" onClick={renderSample} disabled={busy === "sample"}>{busy === "sample" ? "Building…" : "Preview"}</Btn>
                    <Btn onClick={() => emailSample(sampleInvoice.trim(), sampleOffset)} disabled={!sampleInvoice.trim() || busy === `mail:${sampleInvoice.trim()}`}>
                      {busy === `mail:${sampleInvoice.trim()}` ? "Sending…" : "E-mail it to me"}
                    </Btn>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>
                    The preview uses what is on this page right now, saved or not, and shows the e-mail <b style={{ color: C.text }}>and the invoice copy attached to it</b>. “E-mail it to me” sends the real thing, attachment and all, to your own inbox only.
                  </div>

                  {sample && (
                    <div style={{ marginTop: 14 }}>
                      {sample.wouldBe && sample.wouldBe.action !== "send" && (
                        <div style={{ background: C.yellowDim, color: C.yellow, borderRadius: 8, padding: "8px 10px", fontSize: 12.5, marginBottom: 10, lineHeight: 1.45 }}>
                          As things stand this one would not be sent: {sample.wouldBe.reason}.
                        </div>
                      )}
                      <div style={{ background: "#ffffff", color: "#222", borderRadius: 10, padding: "14px 16px", fontSize: 13, overflowX: "auto" }}>
                        <div style={{ borderBottom: "1px solid #e3e6ea", paddingBottom: 10, marginBottom: 12, lineHeight: 1.6, fontFamily: "Arial, Helvetica, sans-serif" }}>
                          <div><span style={{ color: "#777" }}>From </span>{sample.from}</div>
                          <div><span style={{ color: "#777" }}>To </span>{(sample.to || []).join(", ") || "—"}</div>
                          {(sample.cc || []).length > 0 && <div><span style={{ color: "#777" }}>Cc </span>{sample.cc.join(", ")}</div>}
                          <div style={{ fontWeight: 700, marginTop: 4 }}>{sample.subject}</div>
                          {/* The attachment as a mail program shows it: a file you can click, not a promise of one. */}
                          {sample.attached ? (
                            <a href={sample.attached.url} target="_blank" rel="noopener noreferrer" title="Open the attached invoice copy"
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 8, maxWidth: "100%", boxSizing: "border-box", padding: "6px 10px", border: "1px solid #d5dae1", borderRadius: 8, background: "#f6f8fa", color: "#222", textDecoration: "none", lineHeight: 1.35 }}>
                              <span aria-hidden="true">📎</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block", fontWeight: 700, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sample.attached.name}</span>
                                <span style={{ display: "block", color: "#667085", fontSize: 11.5 }}>
                                  PDF attached · {sample.attached.pages} page{sample.attached.pages === 1 ? "" : "s"} · {fileSize(sample.attached.bytes)}
                                </span>
                              </span>
                            </a>
                          ) : sample.pdfError ? (
                            <div style={{ color: "#b42318", fontSize: 12, fontWeight: 700, marginTop: 4 }}>📎 Nothing attached — the invoice copy could not be drawn</div>
                          ) : (
                            <div style={{ color: "#777", fontSize: 12, marginTop: 2 }}>📎 {sample.attachment}</div>
                          )}
                          {(sample.alsoAttached || []).length > 0 && (
                            <div style={{ color: "#667085", fontSize: 12, marginTop: 6 }}>
                              📎 + {sample.alsoAttached.length} more PDF{sample.alsoAttached.length === 1 ? "" : "s"} attached — a copy of each invoice listed below
                            </div>
                          )}
                        </div>
                        {/* Built by the server, which escapes every value it puts in. */}
                        <div dangerouslySetInnerHTML={{ __html: sample.html }} />
                      </div>
                      {/* What the list of their other invoices did, or did not, take in. */}
                      {sample.others && sample.others.error && (
                        <div style={{ background: C.yellowDim, color: C.yellow, borderRadius: 8, padding: "8px 10px", fontSize: 12.5, marginTop: 10, lineHeight: 1.45 }}>
                          The list of their other unpaid invoices could not be built just now ({sample.others.error}). The reminder would still go, without the list.
                        </div>
                      )}
                      {sample.others && !sample.others.error && (
                        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 10, lineHeight: 1.55 }}>
                          {sample.others.listed.length > 0
                            ? <>This reminder day also lists their <b style={{ color: C.text }}>{sample.others.listed.length} other unpaid invoice{sample.others.listed.length === 1 ? "" : "s"}</b>. A client gets that list once a day: if two of its invoices reach this day together, the second reminder goes without it.</>
                            : <>This reminder day would also list their other unpaid invoices — there are none to list right now.</>}
                          {sample.others.leftOut.length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              <b style={{ color: C.yellow }}>Left off the list:</b>
                              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                                {sample.others.leftOut.map((x) => <li key={`${x.invoice}-${x.reason}`}>#{x.invoice} — {x.reason}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </div>

              {/* The attachment itself, full width under both columns: the same bytes a send would attach. */}
              {sample && sample.attached && (
                <div style={{ flex: "1 1 100%", minWidth: 0 }}>
                  <Card>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div style={{ flex: "1 1 380px", minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Attached to this reminder</div>
                        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5, wordBreak: "break-word" }}>
                          <b style={{ color: C.text }}>{sample.attached.name}</b> — PDF, {sample.attached.pages} page{sample.attached.pages === 1 ? "" : "s"}, {fileSize(sample.attached.bytes)}.
                          This is the file the customer gets: the copy of invoice #{sample.invoice}, drawn from Jobber as it stands right now. The real one is drawn again at the moment it is sent.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <LinkBtn kind="blue" href={sample.attached.url}>Open in a new tab</LinkBtn>
                        <LinkBtn href={sample.attached.url} download={sample.attached.name}>Download</LinkBtn>
                      </div>
                    </div>
                    {(sample.alsoAttached || []).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                          Also attached — a copy of each listed invoice ({sample.alsoAttached.length})
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {sample.alsoAttached.map((f) => (
                            <a key={f.url} href={f.url} target="_blank" rel="noopener noreferrer" title="Open this copy in a new tab"
                              style={{ display: "inline-block", maxWidth: "100%", boxSizing: "border-box", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12.5, color: C.text, textDecoration: "none", lineHeight: 1.35 }}>
                              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📎 {f.name}</span>
                              <span style={{ display: "block", color: C.muted, fontSize: 11.5 }}>{f.pages} page{f.pages === 1 ? "" : "s"} · {fileSize(f.bytes)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {sample.attached.inPage ? (
                      <iframe title="The attached invoice copy" src={`${sample.attached.url}#navpanes=0&view=FitH`}
                        style={{ display: "block", width: "100%", height: "min(1000px, 85vh)", marginTop: 12, border: `1px solid ${C.border}`, borderRadius: 8, background: "#fff" }} />
                    ) : (
                      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                        This browser cannot show a PDF inside the page — use “Open in a new tab”, or “E-mail it to me” to get the reminder and its attachment in your own inbox.
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ── When & from whom ──────────────────────────────────────────── */}
          {tab === "sending" && (
            <div>
              <Card style={{ borderColor: draft.enabled ? C.accent : C.border }}>
                <Toggle on={draft.enabled} onChange={(v) => patch({ enabled: v })}
                  label={draft.enabled ? "Invoice reminders are ON" : "Invoice reminders are OFF"}
                  hint="The master switch. While it is off nothing is sent to anyone, whatever else this page says. It takes effect when you Save." />
              </Card>

              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Who they come from</div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 260px" }}>
                    <Field label="Sent from" hint={`A mailbox at ${view.companyDomain}. The reminders appear in this person's Sent folder and replies come back to them.`}>
                      <input value={draft.from_email || ""} onChange={(e) => patch({ from_email: e.target.value })} placeholder={`name@${view.companyDomain}`} style={inputStyle} />
                    </Field>
                  </div>
                  <div style={{ flex: "1 1 220px" }}>
                    <Field label="Name shown">
                      <input value={draft.from_name || ""} onChange={(e) => patch({ from_name: e.target.value })} style={inputStyle} />
                    </Field>
                  </div>
                </div>
                <Field label="Always copy" hint="People here who are cc'd on every reminder. Leave empty if the Sent folder is record enough.">
                  <EmailChips value={draft.internal_cc} onChange={(v) => patch({ internal_cc: v })} placeholder={`someone@${view.companyDomain}`} />
                </Field>
                <Field label="Tell these people when a reminder is held" hint="A reminder that could not go — no PO, Jobber's own still on — is e-mailed to them with the reason, once.">
                  <EmailChips value={draft.notify_emails} onChange={(v) => patch({ notify_emails: v })} placeholder={`someone@${view.companyDomain}`} />
                </Field>
              </Card>

              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>When they go</div>
                <Field label="Reminder days" hint="Days after the due date. 0 is the due date itself. Jobber's own two reminders go on the due date and four days after, and it stops there. Add later days — 15, 30 — to keep reminding about an invoice that is still unpaid.">
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {(draft.offsets_days || []).map((n) => (
                      <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 999, padding: "4px 6px 4px 12px", fontSize: 13 }}>
                        {offsetLabel(n)}
                        {/* A day taken away takes its list of other invoices with it. */}
                        <button type="button" aria-label={`Remove ${offsetLabel(n)}`} onClick={() => patch({ offsets_days: draft.offsets_days.filter((x) => x !== n), list_others_days: (draft.list_others_days || []).filter((x) => x !== n) })}
                          style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                    <input type="number" min={0} max={120} placeholder="+ days" onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const n = Number(e.target.value);
                      if (e.target.value !== "" && Number.isInteger(n) && n >= 0 && n <= 120 && !draft.offsets_days.includes(n)) patch({ offsets_days: [...draft.offsets_days, n].sort((a, b) => a - b) });
                      e.target.value = "";
                    }} style={{ ...inputStyle, width: 90 }} />
                    <span style={{ fontSize: 12, color: C.muted }}>type a number, press Enter</span>
                  </div>
                </Field>
                <Field label="Also list their other unpaid invoices on"
                  hint="A reminder is about one invoice. On the days ticked here it also lists everything else that client has open — past due and not yet due — with invoice number, PO, dates and amount. A client gets the list once a day, however many of its invoices reach that day. An invoice that would be held on its own (waiting on a PO, no PO number) is left off the list.">
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(draft.offsets_days || []).map((n) => {
                      const ticked = (draft.list_others_days || []).includes(n);
                      return (
                        <label key={n} style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", userSelect: "none", background: ticked ? C.accentDim : "transparent", border: `1px solid ${ticked ? C.accent : C.border}`, color: ticked ? C.accent : C.text, borderRadius: 999, padding: "4px 12px 4px 9px", fontSize: 13 }}>
                          <input type="checkbox" checked={ticked} style={{ accentColor: C.accent, margin: 0 }}
                            onChange={() => patch({ list_others_days: ticked ? draft.list_others_days.filter((x) => x !== n) : [...(draft.list_others_days || []), n].sort((a, b) => a - b) })} />
                          the reminder {offsetLabel(n)}
                        </label>
                      );
                    })}
                  </div>
                </Field>
                {(draft.list_others_days || []).length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <Toggle on={draft.list_others_attach !== false} onChange={(v) => patch({ list_others_attach: v })}
                      label="Attach a copy of each listed invoice"
                      hint="Each one as its own PDF, named with its invoice number and PO. Off: the list still goes in the e-mail, and only the reminder's own invoice is attached." />
                  </div>
                )}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 150px" }}>
                    <Field label="Not before" hint="Hour of the day, your time.">
                      <select value={draft.send_hour_local} onChange={(e) => patch({ send_hour_local: Number(e.target.value) })} style={inputStyle}>
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h === 0 ? "12 am" : h < 12 ? `${h} am` : h === 12 ? "12 pm" : `${h - 12} pm`}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div style={{ flex: "0 0 150px" }}>
                    <Field label="Not after">
                      <select value={draft.send_until_hour_local} onChange={(e) => patch({ send_until_hour_local: Number(e.target.value) })} style={inputStyle}>
                        {Array.from({ length: 24 }, (_, k) => k + 1).map((h) => <option key={h} value={h}>{h < 12 ? `${h} am` : h === 12 ? "12 pm" : h === 24 ? "midnight" : `${h - 12} pm`}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div style={{ flex: "1 1 240px" }}>
                    <Field label="If a day is missed" hint="How many days late a reminder may still go out, e.g. after an outage. After that it is skipped, never sent stale.">
                      <select value={draft.grace_days} onChange={(e) => patch({ grace_days: Number(e.target.value) })} style={{ ...inputStyle, width: "auto" }}>
                        {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n === 0 ? "Skip it" : `Send up to ${n} day${n === 1 ? "" : "s"} late`}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                  Reminders are checked every hour between 10 am and 4 pm Pacific. Jobber sends its own a little after 10 am.
                </div>
              </Card>

              <Card>
                <Toggle on={draft.track_opens} onChange={(v) => patch({ track_opens: v })}
                  label="Record when a reminder is opened"
                  hint="Adds an invisible 1-pixel image to each reminder; when their mail program loads it, the open shows under “Sent & opened”. Three honest limits: it cannot say WHO opened it (one e-mail goes to everyone on it — you get a rough location instead); many corporate mail programs block pictures, so “no open recorded” does not mean unread; and scanners or your own Sent folder can load it too, which shows as “possibly opened”." />
              </Card>

              <Card>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>The top of the invoice copy</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.45 }}>Jobber cannot hand its invoice PDF to another program, so the attached copy is drawn here from the same invoice, to the same layout. These lines are printed at the top and bottom of it.</div>
                <Field label="Company name"><input value={(draft.company || {}).name || ""} onChange={(e) => patchCompany("name", e.target.value)} style={inputStyle} /></Field>
                <Field label="Address line"><input value={(draft.company || {}).address_line || ""} onChange={(e) => patchCompany("address_line", e.target.value)} style={inputStyle} /></Field>
                <Field label="Phone / e-mail / website line"><input value={(draft.company || {}).contact_line || ""} onChange={(e) => patchCompany("contact_line", e.target.value)} style={inputStyle} /></Field>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 260px" }}>
                    <Field label="Tax number line" hint="Printed under the payment terms, exactly as typed."><input value={(draft.company || {}).tax_id_line || ""} onChange={(e) => patchCompany("tax_id_line", e.target.value)} style={inputStyle} /></Field>
                  </div>
                  <div style={{ flex: "0 0 150px" }}>
                    <Field label="Bar colour">
                      <input type="color" value={(draft.company || {}).accent || "#4697C9"} onChange={(e) => patchCompany("accent", e.target.value)} style={{ ...inputStyle, padding: 2, height: 36 }} />
                    </Field>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ── Coming up ─────────────────────────────────────────────────── */}
          {tab === "upcoming" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, flex: "1 1 400px" }}>
                  Every unpaid invoice for the clients on this page, read from Jobber just now, with the reminders it has left. Uses the <b style={{ color: C.text }}>saved</b> settings.
                </div>
                <Btn onClick={loadUpcoming} disabled={upcomingBusy}>{upcomingBusy ? "Reading Jobber…" : "Refresh"}</Btn>
              </div>
              {!upcoming && <Card><span style={{ color: C.muted, fontSize: 13.5 }}>{upcomingBusy ? "Reading the open invoices from Jobber — this takes a few seconds…" : "Nothing loaded yet."}</span></Card>}
              {upcoming && (upcoming.clients || []).map((c) => (
                <Card key={c.client_id}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                    <div style={{ fontSize: 15, fontWeight: 800 }}>{c.name}</div>
                    <div style={{ fontSize: 12.5, color: C.muted }}>
                      {c.invoices.length} unpaid · {money(c.invoices.reduce((s, x) => s + (Number(x.balance) || 0), 0))} · to {(c.to || []).join(", ") || "nobody yet"}
                    </div>
                  </div>
                  {c.blockedBy && <div style={{ marginTop: 8, fontSize: 12.5, color: C.yellow, lineHeight: 1.45 }}>{c.blockedBy}. The rows below show what each invoice gets once it is.</div>}
                  {!c.active_from && <div style={{ marginTop: 8, fontSize: 12.5, color: C.yellow }}>No start date yet, so nothing is sent for this client.</div>}
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                    {c.invoices.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Nothing unpaid. 🎉</div>}
                    {c.invoices.map((x) => {
                      // `list`: that reminder also carries the list of the client's other unpaid invoices.
                      const listMark = (u) => (u && u.list ? " (with the list of their other invoices)" : "");
                      const next = x.today ? `Today — ${offsetLabel(x.today.offset)}${listMark(x.today)}` : (x.upcoming && x.upcoming[0]) ? `${fmtDay(x.upcoming[0].date)}${listMark(x.upcoming[0])}` : null;
                      return (
                        <div key={x.invoice} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
                            <div style={{ fontSize: 14 }}>
                              <b>#{x.invoice}</b> <span style={{ color: C.muted }}>·</span> {money(x.balance)} <span style={{ color: C.muted }}>·</span>{" "}
                              <span style={{ color: x.daysPastDue > 0 ? C.red : C.text }}>{x.daysPastDue > 0 ? `${x.daysPastDue} day${x.daysPastDue === 1 ? "" : "s"} past due` : `due ${fmtDay(x.due)}`}</span>
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <Btn size="sm" onClick={() => viewCopy(x.invoice)} disabled={busy === `pdf:${x.invoice}`}>{busy === `pdf:${x.invoice}` ? "Drawing…" : "Invoice copy"}</Btn>
                              <Btn size="sm" onClick={() => emailSample(x.invoice)} disabled={busy === `mail:${x.invoice}`}>{busy === `mail:${x.invoice}` ? "Sending…" : "E-mail me a sample"}</Btn>
                            </div>
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
                            {x.po ? <>PO <b style={{ color: C.text }}>{x.po}</b> <span>(from the {x.poSource})</span></> : <b style={{ color: C.yellow }}>No PO number found</b>}
                            {" · "}
                            {x.wouldHoldBecause
                              ? <span style={{ color: C.yellow }}>Would be held: {x.wouldHoldBecause}</span>
                              : next ? <span>Next reminder: <b style={{ color: C.text }}>{next}</b>{(() => {
                                const later = (x.upcoming || []).slice(x.today ? 0 : 1).map((u) => `${fmtDay(u.date)}${u.list ? " (with the list)" : ""}`);
                                return later.length ? `, then ${later.join(", ")}` : "";
                              })()}</span>
                                : <span>No reminders left — both days have passed{x.daysPastDue > 0 ? "; this one is for a personal follow-up" : ""}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* ── Sent & opened ─────────────────────────────────────────────── */}
          {tab === "sent" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, flex: "1 1 400px" }}>
                  Every reminder ElecBid has sent or held, newest first.{" "}
                  {view.settings.track_opens ? "Opens are recorded when their mail program loads the pictures." : "Open tracking is off — switch it on under “When & from whom”."}
                </div>
                <Btn onClick={loadActivity}>Refresh</Btn>
              </div>
              {!activity && <Card><span style={{ color: C.muted, fontSize: 13.5 }}>Loading…</span></Card>}
              {activity && activity.length === 0 && <Card><span style={{ color: C.muted, fontSize: 13.5 }}>Nothing has been sent yet.</span></Card>}
              {activity && activity.map((r) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.held;
                const op = r.open ? (OPEN_STYLE[r.open.state] || OPEN_STYLE.none) : null;
                const isOpen = openRow === r.id;
                return (
                  <Card key={r.id} style={{ padding: "12px 16px" }}>
                    <div role="button" tabIndex={0} onClick={() => setOpenRow(isOpen ? null : r.id)} onKeyDown={(e) => { if (e.key === "Enter") setOpenRow(isOpen ? null : r.id); }}
                      style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14 }}>
                          <b>#{r.invoice_number}</b> <span style={{ color: C.muted }}>·</span> {r.client_name} <span style={{ color: C.muted }}>·</span> {money(r.balance)}
                          {r.po_number ? <span style={{ color: C.muted }}> · PO {r.po_number}</span> : null}
                        </div>
                        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>
                          {r.status === "sent" ? `Sent ${fmtWhen(r.sent_at)}` : `${st.word} ${fmtWhen(r.updated_at)}`} · the reminder {offsetLabel(r.offset_days)}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        {op && <Pill color={op.color} bg={op.bg} title={r.open.detail}>{op.word}{r.open.count > 1 ? ` · ${r.open.count}×` : ""}</Pill>}
                        {r.status === "sent" && !r.tracked && <Pill title="This one was sent with open tracking switched off.">Not tracked</Pill>}
                        <Pill color={st.color} bg={st.bg}>{st.word}</Pill>
                        <span style={{ color: C.muted, fontSize: 12 }}>{isOpen ? "▾" : "▸"}</span>
                      </div>
                    </div>
                    {r.status !== "sent" && r.reason && <div style={{ marginTop: 8, fontSize: 12.5, color: r.status === "failed" ? C.red : C.yellow, lineHeight: 1.45 }}>{r.reason}</div>}

                    {isOpen && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 12.5, lineHeight: 1.6 }}>
                        <div style={{ color: C.muted }}>
                          From <span style={{ color: C.text }}>{r.from_email || "—"}</span> · To <span style={{ color: C.text }}>{(r.to_emails || []).join(", ") || "—"}</span>
                          {(r.cc_emails || []).length > 0 && <> · Cc <span style={{ color: C.text }}>{r.cc_emails.join(", ")}</span></>}
                        </div>
                        {r.subject && <div style={{ marginTop: 4 }}><span style={{ color: C.muted }}>Subject </span>{r.subject}</div>}
                        {r.attachment_name && <div style={{ color: C.muted }}>📎 {r.attachment_name}{r.others && r.others.attached ? ` + ${r.others.attached} more` : ""}</div>}
                        {/* What the list of the client's other invoices held, when this reminder carried one. */}
                        {r.others && r.others.error && <div style={{ color: C.yellow, marginTop: 4 }}>{r.others.error}</div>}
                        {r.others && (r.others.listed || []).length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <span style={{ color: C.muted }}>Also listed {r.others.listed.length} other unpaid invoice{r.others.listed.length === 1 ? "" : "s"}{r.others.attached ? ", a copy of each attached" : ""}: </span>
                            {r.others.listed.map((x) => `#${x.invoice}${x.po ? ` (PO ${x.po})` : ""} ${money(x.balance)}${x.days_past_due > 0 ? `, ${x.days_past_due} day${x.days_past_due === 1 ? "" : "s"} past due` : ""}`).join(" · ")}
                          </div>
                        )}
                        {r.others && (r.others.left_out || []).length > 0 && (
                          <div style={{ marginTop: 4, color: C.muted }}>
                            Left off the list: {r.others.left_out.map((x) => `#${x.invoice} — ${x.reason}`).join("; ")}
                          </div>
                        )}

                        {r.open && (
                          <div style={{ marginTop: 10, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                            <div style={{ fontWeight: 700, color: op ? op.color : C.text }}>{r.open.label}{r.open.firstAt ? ` — ${fmtWhen(r.open.firstAt)}` : ""}</div>
                            <div style={{ color: C.muted, marginTop: 2 }}>{r.open.detail}</div>
                            {(r.opens || []).length > 0 && (
                              <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
                                {r.opens.slice().reverse().map((e, k) => (
                                  <div key={`${e.at}-${k}`} style={{ color: C.muted }}>
                                    <span style={{ color: C.text }}>{fmtWhen(e.at)}</span> · {VIA_WORD[e.via] || e.via}{e.where ? ` · ${e.where}` : ""}{e.auto ? " · within a minute of sending (automatic)" : ""}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {r.body_text && (
                          <pre style={{ marginTop: 10, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", fontSize: 12.5, color: C.text }}>{r.body_text}</pre>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Save bar — only when something is waiting to be kept */}
      {dirty && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, background: "rgba(22,26,32,.97)", borderTop: `1px solid ${C.border}`, padding: "10px 20px", zIndex: 50 }}>
          <div style={{ maxWidth: 940, margin: "0 auto", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: C.yellow }}>You have changes that are not saved yet. Nothing changes for customers until you save.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={discard} disabled={busy === "save"}>Discard</Btn>
              <Btn kind="accent" onClick={save} disabled={busy === "save"}>{busy === "save" ? "Saving…" : "Save changes"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
